import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import $ from "jquery";
import ApiURl from "../controllers/Api";
import imgLocation from "../controllers/imagePath";
import { gsap } from "gsap";
import Flip from "gsap/Flip";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion, AnimatePresence } from "framer-motion";
import ReviewModal from "./ReviewModal";
import ReviewCard from "./ReviewCard";

gsap.registerPlugin(Flip, ScrollTrigger);

// Inline fallback (no external DNS)
const FALLBACK_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="300" height="300">
    <rect width="100%" height="100%" fill="#f2f2f2"/>
    <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
      font-family="Arial" font-size="18" fill="#777">
      No Image
    </text>
  </svg>
`);

async function postPhp(url, payload) {
  try {
    const res = await $.ajax({
      url,
      method: "POST",
      contentType: "application/json",
      dataType: "json",
      data: JSON.stringify(payload),
    });
    return res;
  } catch (jsonErr) {
    const res = await $.ajax({
      url,
      method: "POST",
      dataType: "json",
      data: payload,
    });
    return res;
  }
}

// Random Product Card Component with Discount Support and Stock Status
const RandomProductCard = ({ product, onClick }) => {
  const cardRef = useRef(null);

  const joinUrl = (base, path) => {
    if (!path) return "";
    if (typeof path !== "string") return "";
    if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:")) {
      return path;
    }
    const b = String(base || "").replace(/\/+$/, "");
    const pth = path.replace(/^\/+/, "");
    if (!b) return `/${pth}`;
    return `${b}/${pth}`;
  };

  const getProductImage = (product) => {
    if (product?.images && Array.isArray(product.images) && product.images.length > 0) {
      const first = product.images[0];
      const candidate = first?.url || first?.filename || first?.image_url || first;
      const url = joinUrl(imgLocation, candidate);
      return url || FALLBACK_IMG;
    }

    if (product?.image) {
      const url = joinUrl(imgLocation, product.image);
      return url || FALLBACK_IMG;
    }

    const raw = product?.img_path || product?.image_path;
    if (!raw) return FALLBACK_IMG;

    if (
      typeof raw === "string" &&
      (raw.startsWith("http://") || raw.startsWith("https://") || raw.startsWith("data:"))
    ) {
      return raw;
    }

    if (typeof raw === "string") {
      try {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length > 0) {
          const firstImage = String(arr[0] || "").trim();
          const url = joinUrl(imgLocation, firstImage);
          return url || FALLBACK_IMG;
        }
      } catch (e) {}

      const parts = raw
        .split(",")
        .map((p) => String(p || "").trim())
        .filter(Boolean);

      if (parts.length > 0) {
        const url = joinUrl(imgLocation, parts[0]);
        return url || FALLBACK_IMG;
      }

      const url = joinUrl(imgLocation, raw);
      return url || FALLBACK_IMG;
    }

    return FALLBACK_IMG;
  };

  const getProductTitle = (p) => p?.name || p?.product_name || p?.productName || "Product";

  const getStockStatusBadge = (stock) => {
    if (stock <= 0) {
      return (
        <span className="absolute top-2 right-2 z-10 bg-red-500 text-white text-[10px] px-2 py-1 rounded-full">
          Out of Stock
        </span>
      );
    }
    if (stock < 10) {
      return (
        <span className="absolute top-2 right-2 z-10 bg-orange-500 text-white text-[10px] px-2 py-1 rounded-full">
          Only {stock} left!
        </span>
      );
    }
    if (stock < 20) {
      return (
        <span className="absolute top-2 right-2 z-10 bg-yellow-500 text-white text-[10px] px-2 py-1 rounded-full">
          Few left!
        </span>
      );
    }
    return null;
  };

  const getEffectivePriceForProduct = (product) => {
    if (product?.has_discount && product?.discounted_price && product?.original_price) {
      return {
        final: parseFloat(product.discounted_price),
        original: parseFloat(product.original_price),
        hasDiscount: true,
        percent: product.discount_percent || 0,
      };
    }

    if (product?.prices && Array.isArray(product.prices) && product.prices.length > 0) {
      let bestPrice = Infinity;
      let bestOriginal = Infinity;
      let bestPercent = 0;
      let foundDiscount = false;

      product.prices.forEach((priceItem) => {
        const originalPrice = parseFloat(priceItem.price || 0);
        if (isNaN(originalPrice) || originalPrice <= 0) return;

        let finalPrice = originalPrice;

        const discType = (priceItem.single_discount_type || "").toString().trim();
        const discValue = parseFloat(priceItem.single_discount_value || 0);

        if (discType && !isNaN(discValue) && discValue > 0) {
          if (discType === "percentage") {
            finalPrice = originalPrice * (1 - discValue / 100);
          } else if (discType === "amount" || discType === "fixed") {
            finalPrice = originalPrice - discValue;
          }
          finalPrice = Math.max(0, finalPrice);
        }

        if (finalPrice < bestPrice) {
          bestPrice = finalPrice;
          bestOriginal = originalPrice;
        }
      });

      if (bestPrice !== Infinity) {
        if (bestPrice < bestOriginal) {
          foundDiscount = true;
          bestPercent = Math.round(((bestOriginal - bestPrice) / bestOriginal) * 100);
        }

        return {
          final: bestPrice,
          original: bestOriginal,
          hasDiscount: foundDiscount,
          percent: bestPercent,
        };
      }
    }

    if (product?.prices_string) {
      const priceArray = String(product.prices_string)
        .split(",")
        .map((p) => parseFloat(p));

      const discountTypes = String(product.single_discount_types || "").split(",");
      const discountValues = String(product.single_discount_values || "").split(",");

      let bestFinal = Infinity;
      let bestOriginal = Infinity;
      let bestPercent = 0;
      let foundDiscount = false;

      priceArray.forEach((price, idx) => {
        if (isNaN(price) || price <= 0) return;

        const originalPrice = price;
        let finalPrice = originalPrice;

        const discountType = (discountTypes[idx] || "").trim();
        const discountValue = parseFloat(discountValues[idx] || 0);

        if (discountType && !isNaN(discountValue) && discountValue > 0) {
          if (discountType === "percentage") {
            finalPrice = originalPrice * (1 - discountValue / 100);
          } else if (discountType === "amount" || discountType === "fixed") {
            finalPrice = originalPrice - discountValue;
          }
          finalPrice = Math.max(0, finalPrice);
        }

        if (finalPrice < bestFinal) {
          bestFinal = finalPrice;
          bestOriginal = originalPrice;
        }
      });

      if (bestFinal !== Infinity) {
        if (bestFinal < bestOriginal) {
          foundDiscount = true;
          bestPercent = Math.round(((bestOriginal - bestFinal) / bestOriginal) * 100);
        }

        return {
          final: bestFinal,
          original: bestOriginal,
          hasDiscount: foundDiscount,
          percent: bestPercent,
        };
      }
    }

    const priceValue = parseFloat(product?.min_price || product?.price || 0);
    return {
      final: priceValue,
      original: priceValue,
      hasDiscount: false,
      percent: 0,
    };
  };

  // FIXED: This function now correctly uses the product prop
  const formatPriceForRandomCard = (product) => {
    const priceInfo = getEffectivePriceForProduct(product);

    if (priceInfo.final === 0 || isNaN(priceInfo.final)) {
      return "Price on request";
    }

    if (priceInfo.hasDiscount && priceInfo.final < priceInfo.original) {
      return (
        <div className="flex items-center gap-1">
          <span className="text-[#b29063] font-bold">₹{Math.round(priceInfo.final)}</span>
          <span className="text-gray-400 line-through text-[10px]">₹{Math.round(priceInfo.original)}</span>
        </div>
      );
    }

    return <span className="text-[#b29063] font-bold">₹{Math.round(priceInfo.final)}</span>;
  };

  const getDiscountBadgeForProduct = (product) => {
    const priceInfo = getEffectivePriceForProduct(product);

    if (priceInfo.hasDiscount && priceInfo.percent > 0) {
      return (
        <span className="absolute top-2 left-2 z-10 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
          {priceInfo.percent}% OFF
        </span>
      );
    }

    return null;
  };

  const imageUrl = getProductImage(product);
  const title = getProductTitle(product);
  const productId = product?.product_id || product?.id;
  // FIXED: Use the correct function name
  const priceDisplay = formatPriceForRandomCard(product);
  const discountBadge = getDiscountBadgeForProduct(product);
  const stockBadge = getStockStatusBadge(product.stock_quantity);

  const onCardEnter = () => {
    if (cardRef.current) {
      gsap.to(cardRef.current, {
        y: -10,
        boxShadow: "0px 45px 60px 0px rgba(178, 144, 99, 0.1)",
        duration: 0.22,
        ease: "power2.out",
      });
    }
  };

  const onCardLeave = () => {
    if (cardRef.current) {
      gsap.to(cardRef.current, {
        y: 0,
        boxShadow: "0px 15px 20px 0px rgba(178, 144, 99, 0.05)",
        duration: 0.22,
        ease: "power2.out",
      });
    }
  };

  return (
    <div
      ref={cardRef}
      className="relative flex flex-col bg-white p-2 shadow-[0px_15px_20px_0px_rgba(178,144,99,0.05)] will-change-transform hover:shadow-lg transition-shadow duration-200 border border-[#b29063]/10 cursor-pointer"
      onMouseEnter={onCardEnter}
      onMouseLeave={onCardLeave}
      onClick={() => onClick(productId)}
    >
      <div className="relative w-full">
        <div className="absolute left-0 top-0 z-[2] flex w-full items-center justify-between p-2 text-[11px] tracking-[0.1em] text-gray-700 bg-white/80 backdrop-blur-sm border-b border-[#b29063]/10">
          <span className="uppercase truncate max-w-[55%] font-semibold text-gray-900">
            {product.category_name || product.category || "Product"}
          </span>
          <span className="uppercase font-bold text-[#b29063]">{priceDisplay}</span>
        </div>

        <div className="w-full h-40 flex items-center justify-center bg-gradient-to-br from-[#b29063]/5 to-[#b29063]/10 mt-8 relative">
          {discountBadge}
          {stockBadge}
          <img
            src={imageUrl}
            alt={title}
            className="block max-h-[140px] w-full max-w-full object-contain"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = FALLBACK_IMG;
            }}
          />
        </div>
      </div>

      <div className="mt-0 px-2 pt-1 pb-2">
        <p className="line-clamp-1 text-[12px] font-semibold tracking-[0.06em] text-gray-900">{title}</p>
        <p className="mt-0 line-clamp-2 text-[10px] tracking-[0.08em] text-gray-600">
          {product.description || "No description available"}
        </p>
        {product.stock_quantity > 0 && product.stock_quantity < 20 && (
          <p className="mt-1 text-[10px] font-medium text-orange-600">
            {product.stock_quantity < 10 ? `🔥 Only ${product.stock_quantity} left!` : "📦 Few left!"}
          </p>
        )}
      </div>
    </div>
  );
};

const ProductOverview = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState({});
  const [cartMessage, setCartMessage] = useState("");
  const [cartId] = useState(localStorage.getItem("cart_id"));

  const [sizes, setSizes] = useState([]);
  const [prices, setPrices] = useState([]);
  const [quantities, setQuantities] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);

  const [singleDiscountTypes, setSingleDiscountTypes] = useState([]);
  const [singleDiscountValues, setSingleDiscountValues] = useState([]);
  const [bulkDiscounts, setBulkDiscounts] = useState({});
  const [appliedBulkDiscounts, setAppliedBulkDiscounts] = useState({});

  const [cartTotal, setCartTotal] = useState(0);

  const [randomProducts, setRandomProducts] = useState([]);
  const [loadingRandom, setLoadingRandom] = useState(false);

  const [wishlistAdded, setWishlistAdded] = useState(false);
  const [wishlistBusy, setWishlistBusy] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("Item Added to Wishlist");
  const [userData, setUserData] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({
    total_reviews: 0,
    average_rating: 0,
    rating_counts: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
  });
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [userPurchaseStatus, setUserPurchaseStatus] = useState({
    has_purchased: false,
    can_review: false,
    existing_review: null,
  });
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewPagination, setReviewPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_reviews: 0,
  });
  const [selectedRatingFilter, setSelectedRatingFilter] = useState("all");

  const pageRef = useRef(null);
  const mediaCardRef = useRef(null);
  const detailsCardRef = useRef(null);
  const rowsRef = useRef([]);
  const randomSectionRef = useRef(null);

  const mainImageRef = useRef(null);
  const thumbElsRef = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const safeFormatPrice = (price) => {
    if (price === undefined || price === null || isNaN(price)) {
      return "0.00";
    }
    const numPrice = typeof price === "string" ? parseFloat(price) : price;
    return !isNaN(numPrice) ? numPrice.toFixed(2) : "0.00";
  };

  const normalizePurityOptions = (rawOptions) => {
    let options = rawOptions;

    if (typeof options === "string") {
      try {
        options = JSON.parse(options);
      } catch (e) {
        options = [];
      }
    }

    if (!Array.isArray(options)) return [];

    return options
      .map((item) => ({
        id: item?.id,
        purity_id: item?.purity_id,
        purity_name: String(item?.purity_name || item?.name || "").trim(),
        price: Number(item?.price || 0),
      }))
      .filter((item) => item.purity_name && item.price > 0);
  };

  // FIXED: price logic for ProductOverview with proper bulk discount priority
  const getEffectivePrice = (sizeIndex, quantity = 1) => {
    const originalPrice = parseFloat(prices[sizeIndex] || 0);

    if (isNaN(originalPrice) || originalPrice <= 0) {
      return 0;
    }

    let finalPrice = originalPrice;
    const sizeName = sizes[sizeIndex];

    // First priority: Check for bulk discount (highest priority)
    const bulkDiscount = appliedBulkDiscounts[sizeName];
    if (bulkDiscount) {
      const bulkPrice = parseFloat(bulkDiscount.discounted_price);
      if (!isNaN(bulkPrice) && bulkPrice > 0 && bulkPrice < finalPrice) {
        console.log(`Bulk discount applied for ${sizeName}:`, {
          original: originalPrice,
          bulk: bulkPrice,
          quantity,
          minQty: bulkDiscount.min_quantity
        });
        finalPrice = bulkPrice;
      }
    }

    // Second priority: Single discount (only if no bulk discount applies)
    const discountType = (singleDiscountTypes[sizeIndex] || "").toString().trim();
    const discountValueRaw = singleDiscountValues[sizeIndex];
    const discountValue = parseFloat(discountValueRaw);

    if (!bulkDiscount && discountType && !isNaN(discountValue) && discountValue > 0) {
      let discountedPrice = originalPrice;

      if (discountType === "percentage") {
        discountedPrice = originalPrice * (1 - discountValue / 100);
      } else if (discountType === "amount" || discountType === "fixed") {
        discountedPrice = originalPrice - discountValue;
      }

      discountedPrice = Math.max(0, discountedPrice);

      if (discountedPrice < finalPrice) {
        finalPrice = discountedPrice;
      }
    }

    return finalPrice;
  };

  // FIXED: formatPriceWithDiscount function
  const formatPriceWithDiscount = (sizeIndex, quantity = 1) => {
    const originalPrice = parseFloat(prices[sizeIndex] || 0);
    const effectivePrice = getEffectivePrice(sizeIndex, quantity);
    const sizeName = sizes[sizeIndex];
    const bulkDiscount = appliedBulkDiscounts[sizeName];

    if (isNaN(originalPrice) || originalPrice <= 0) {
      return <span className="text-sm text-gray-500">Price on request</span>;
    }

    if (bulkDiscount && effectivePrice < originalPrice) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2">
        <span className="text-sm font-bold text-[#b29063]">
          ₹{safeFormatPrice(effectivePrice)}
        </span>
        <span className="text-xs text-gray-400 line-through">
          ₹{safeFormatPrice(originalPrice)}
        </span>
      </div>
      <span className="text-[10px] text-[#b29063]/70">
        Bulk price (Min {bulkDiscount.min_quantity})
      </span>
    </div>
  );
}

    if (effectivePrice < originalPrice) {
      return (
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-green-600">
            ₹{safeFormatPrice(effectivePrice)}
          </span>
          <span className="text-sm text-gray-400 line-through">
            ₹{safeFormatPrice(originalPrice)}
          </span>
        </div>
      );
    }

    return (
      <span className="text-sm font-medium text-gray-900">
        ₹{safeFormatPrice(originalPrice)}
      </span>
    );
  };

  const getDiscountBadge = (sizeIndex) => {
    const originalPrice = parseFloat(prices[sizeIndex] || 0);
    if (isNaN(originalPrice) || originalPrice <= 0) return null;

    const sizeName = sizes[sizeIndex];
    const quantity = quantities[sizeIndex] || 1;
    
    // Check for bulk discount first
    const bulkDiscount = appliedBulkDiscounts[sizeName];
    if (bulkDiscount) {
      const bulkPrice = parseFloat(bulkDiscount.discounted_price);
      if (!isNaN(bulkPrice) && bulkPrice > 0 && bulkPrice < originalPrice) {
        const percent = Math.round(((originalPrice - bulkPrice) / originalPrice) * 100);
       
      }
    }

    // Check for single discount
    const discountType = (singleDiscountTypes[sizeIndex] || "").toString().trim();
    const discountValue = parseFloat(singleDiscountValues[sizeIndex]);

    if (discountType && !isNaN(discountValue) && discountValue > 0) {
      let discountedPrice = originalPrice;

      if (discountType === "percentage") {
        discountedPrice = originalPrice * (1 - discountValue / 100);
      } else if (discountType === "amount" || discountType === "fixed") {
        discountedPrice = originalPrice - discountValue;
      }

      discountedPrice = Math.max(0, discountedPrice);

      if (discountedPrice < originalPrice) {
        const percent = Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
        return (
          <span className="ml-2 inline-flex items-center rounded-full bg-green-500 px-2 py-0.5 text-xs font-medium text-white">
            {percent}% OFF
          </span>
        );
      }
    }

    return null;
  };

  const getStockStatusBadge = (stock) => {
    if (stock <= 0) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Out of Stock
        </span>
      );
    }
    if (stock < 10) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          🔥 Only {stock} left!
        </span>
      );
    }
    if (stock < 20) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          📦 Few left!
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        ✓ In Stock
      </span>
    );
  };

  const calculateGrandTotal = () => {
    return selectedSizes.reduce((acc, size) => {
      const sizeIndex = sizes.indexOf(size);
      const quantity = quantities[sizeIndex] || 1;
      const price = getEffectivePrice(sizeIndex, quantity);
      return acc + quantity * price;
    }, 0);
  };

  const finalTotal = useMemo(() => {
    const grandTotal = selectedSizes.reduce((acc, size) => {
      const sizeIndex = sizes.indexOf(size);
      const quantity = quantities[sizeIndex] || 1;
      const price = getEffectivePrice(sizeIndex, quantity);
      return acc + quantity * price;
    }, 0);
    return grandTotal + cartTotal;
  }, [selectedSizes, sizes, quantities, cartTotal, appliedBulkDiscounts, prices, singleDiscountTypes, singleDiscountValues]);

  const firstAvailablePrice = useMemo(() => {
    if (!prices || prices.length === 0) {
      return 0;
    }

    for (let i = 0; i < prices.length; i++) {
      if (availability[i] !== 0 && typeof prices[i] === "number" && !isNaN(prices[i])) {
        const effectivePrice = getEffectivePrice(i, 1);
        return typeof effectivePrice === "number" && !isNaN(effectivePrice) ? effectivePrice : 0;
      }
    }

    const fallbackPrice = prices[0] || 0;
    return typeof fallbackPrice === "number" && !isNaN(fallbackPrice) ? fallbackPrice : 0;
  }, [prices, availability, appliedBulkDiscounts, singleDiscountTypes, singleDiscountValues]);

  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setUserData(user);
        const admin = user?.role === "admin";
        setIsAdmin(admin);
        localStorage.setItem("is_admin", admin ? "1" : "0");
      } catch (e) {
        console.error("Error parsing user data:", e);
      }
    }
  }, []);

  useEffect(() => {
    const prevBody = document.body.style.overflowX;
    const prevHtml = document.documentElement.style.overflowX;
    document.body.style.overflowX = "hidden";
    document.documentElement.style.overflowX = "hidden";
    return () => {
      document.body.style.overflowX = prevBody;
      document.documentElement.style.overflowX = prevHtml;
    };
  }, []);

  useEffect(() => {
    const mobileQuery = window.matchMedia("(max-width: 767px)");
    if (!mobileQuery.matches) return undefined;

    const root = document.getElementById("root");
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyY = document.body.style.overflowY;
    const prevHtmlY = document.documentElement.style.overflowY;
    const prevRootOverflow = root?.style.overflow || "";
    const prevRootY = root?.style.overflowY || "";

    document.documentElement.style.overflowY = "auto";
    document.body.style.overflow = "visible";
    document.body.style.overflowY = "visible";
    if (root) {
      root.style.overflow = "visible";
      root.style.overflowY = "visible";
    }

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.overflowY = prevBodyY;
      document.documentElement.style.overflowY = prevHtmlY;
      if (root) {
        root.style.overflow = prevRootOverflow;
        root.style.overflowY = prevRootY;
      }
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await $.getJSON(`${ApiURl}/getProducts.php?product=${id}`);

        if (response && response.products && response.products.length > 0) {
          const productData = response.products[0];
          const purityOptions = normalizePurityOptions(productData.purity_options);
          const hasPurityPricing = purityOptions.length > 0;

          setProduct({
            ...productData,
            purity_options: purityOptions,
            uses_purity_pricing: hasPurityPricing,
          });

          const parsedSizes = hasPurityPricing
            ? purityOptions.map((item) => item.purity_name)
            : productData.sizes
              ? productData.sizes.split(",")
              : [];
          const parsedPrices = hasPurityPricing
            ? purityOptions.map((item) => Number(item.price))
            : productData.prices
              ? productData.prices.split(",").map((n) => Number(n))
              : [];
          const parsedAvailability = hasPurityPricing
            ? purityOptions.map(() => 1)
            : productData.availability
              ? productData.availability.split(",").map((n) => Number(n))
              : [];

          let parsedSingleDiscountTypes = [];
          let parsedSingleDiscountValues = [];

          if (productData.single_discount_types) {
            if (typeof productData.single_discount_types === "string") {
              parsedSingleDiscountTypes = productData.single_discount_types.split(",");
            } else if (Array.isArray(productData.single_discount_types)) {
              parsedSingleDiscountTypes = productData.single_discount_types;
            }
          }

          if (productData.single_discount_values) {
            if (typeof productData.single_discount_values === "string") {
              parsedSingleDiscountValues = productData.single_discount_values.split(",").map((val) => {
                const trimmed = String(val).trim();
                return trimmed === "" ? "" : parseFloat(trimmed);
              });
            } else if (Array.isArray(productData.single_discount_values)) {
              parsedSingleDiscountValues = productData.single_discount_values.map((val) => {
                const trimmed = String(val).trim();
                return trimmed === "" ? "" : parseFloat(trimmed);
              });
            }
          }

          const parsedBulkDiscounts = productData.bulk_discounts || {};

          setSizes(parsedSizes);
          setPrices(parsedPrices);
          setAvailability(parsedAvailability);
          setSingleDiscountTypes(hasPurityPricing ? new Array(parsedSizes.length).fill("") : parsedSingleDiscountTypes);
          setSingleDiscountValues(hasPurityPricing ? new Array(parsedSizes.length).fill(0) : parsedSingleDiscountValues);
          setBulkDiscounts(hasPurityPricing ? {} : parsedBulkDiscounts);
          setQuantities(new Array(parsedSizes.length).fill(1));
          setSelectedSizes([]);
        } else {
          setCartMessage(response?.message || "No product found.");
        }
      } catch (error) {
        console.error("Error fetching product data:", error);
        setCartMessage("Error fetching product data.");
      }
    };

    if (id) fetchData();
  }, [id]);

  useEffect(() => {
    const newAppliedDiscounts = {};

    selectedSizes.forEach((size) => {
      const sizeIndex = sizes.indexOf(size);
      const quantity = quantities[sizeIndex] || 1;

      if (bulkDiscounts[size] && Array.isArray(bulkDiscounts[size])) {
        const applicableDiscounts = bulkDiscounts[size]
          .filter((d) => d.min_quantity <= quantity)
          .sort((a, b) => b.min_quantity - a.min_quantity);

        if (applicableDiscounts.length > 0) {
          newAppliedDiscounts[size] = applicableDiscounts[0];
        }
      }
    });

    setAppliedBulkDiscounts(newAppliedDiscounts);
  }, [selectedSizes, quantities, bulkDiscounts, sizes]);

  useEffect(() => {
    const fetchCartTotal = async () => {
      if (!cartId) return;
      try {
        const response = await $.getJSON(`${ApiURl}/getCartItems.php?cart_id=${cartId}`);
        if (response && response.data && response.data.length > 0) {
          const totalAmount = response.data.reduce((acc, item) => {
            const price = parseFloat(item.price);
            const quantity = parseInt(item.quantity, 10);
            if (!isNaN(price) && !isNaN(quantity)) return acc + price * quantity;
            return acc;
          }, 0);
          setCartTotal(totalAmount);
        } else {
          setCartTotal(0);
        }
      } catch (error) {
        console.error("Error fetching cart total:", error);
      }
    };

    fetchCartTotal();
  }, [cartId]);

  useEffect(() => {
    const fetchRandomProducts = async () => {
      setLoadingRandom(true);
      setRandomProducts([]);

      try {
        const response = await fetch(`${ApiURl}/getallproducts.php`);
        const data = await response.json();

        let products = [];

        if (data?.products && Array.isArray(data.products)) {
          products = data.products;
        } else if (data?.data && Array.isArray(data.data)) {
          products = data.data;
        } else if (Array.isArray(data)) {
          products = data;
        } else if (data?.result && Array.isArray(data.result)) {
          products = data.result;
        }

        if (products.length > 0) {
          const filteredProducts = products.filter((p) => {
            const productId = p?.product_id || p?.id;
            return productId && String(productId) !== String(id);
          });

          if (filteredProducts.length > 0) {
            const shuffled = [...filteredProducts].sort(() => 0.5 - Math.random());
            const selected = shuffled.slice(0, 8);
            setRandomProducts(selected);
          } else {
            setRandomProducts([]);
          }
        } else {
          setRandomProducts([]);
        }
      } catch (error) {
        console.error("Error fetching random products:", error);
        setRandomProducts([]);
      } finally {
        setLoadingRandom(false);
      }
    };

    if (id) fetchRandomProducts();
  }, [id]);

  useEffect(() => {
    const userId = localStorage.getItem("user_id");
    const productId = product?.product_id != null ? String(product.product_id) : "";
    if (!userId || !productId) {
      setWishlistAdded(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await $.getJSON(`${ApiURl}/getWishlistItems.php?user_id=${userId}`);
        const list = res?.wishlist || [];
        const exists = list.some((it) => String(it?.product_id) === productId);
        if (!cancelled) setWishlistAdded(!!exists);
      } catch (err) {
        console.error("Error loading wishlist state:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [product?.product_id]);

  const checkUserCanReview = async () => {
    const userId = localStorage.getItem("user_id");
    const role = (localStorage.getItem("role") || "").toLowerCase();
    const isAdminRole = role === "admin";

    if (!userId || !product?.product_id) return;

    try {
      const response = await $.ajax({
        url: `${ApiURl}/checkUserPurchase.php`,
        method: "POST",
        contentType: "application/json",
        dataType: "json",
        data: JSON.stringify({
          user_id: userId,
          product_id: product.product_id,
          is_admin: isAdminRole ? 1 : 0,
        }),
      });

      if (response?.success) {
        setUserPurchaseStatus({
          has_purchased: response.has_purchased,
          can_review: response.can_review,
          existing_review: response.existing_review,
          is_admin: response.is_admin,
        });
      }
    } catch (error) {
      console.error("Error checking purchase status:", error);
    }
  };

  const fetchReviews = async (page = 1) => {
    if (!product?.product_id) return;

    setLoadingReviews(true);
    try {
      const response = await $.getJSON(
        `${ApiURl}/getProductReviews.php?product_id=${product.product_id}&page=${page}&limit=5`
      );

      if (response?.success) {
        setReviews(response.reviews || []);
        setReviewStats({
          total_reviews: response.statistics?.total_reviews || 0,
          average_rating: Number(response.statistics?.average_rating || 0),
          rating_counts: response.statistics?.rating_counts || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
        });

        setReviewPagination(
          response.pagination || {
            current_page: 1,
            total_pages: 1,
            total_reviews: 0,
          }
        );
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleReviewSuccess = () => {
    fetchReviews();
    checkUserCanReview();
  };

  const handleEditReview = (review) => {
    setEditingReview(review);
    setShowReviewModal(true);
  };

  const handleDeleteReview = async (review) => {
    if (!window.confirm("Are you sure you want to delete this review?")) return;

    const userId = localStorage.getItem("user_id");
    const isAdminRole = (localStorage.getItem("role") || "").toLowerCase() === "admin";

    try {
      const response = await $.ajax({
        url: `${ApiURl}/deleteReview.php`,
        method: "POST",
        contentType: "application/json",
        dataType: "json",
        data: JSON.stringify({
          review_id: review.id,
          user_id: userId,
          is_admin: isAdminRole ? 1 : 0,
        }),
      });

      if (response?.success) {
        fetchReviews();
        checkUserCanReview();
      }
    } catch (error) {
      console.error("Error deleting review:", error);
    }
  };

  const handleHelpful = (reviewId, newCount) => {
    setReviews(reviews.map((r) => (r.id === reviewId ? { ...r, helpful_count: newCount } : r)));
  };

  const filteredReviews =
    selectedRatingFilter === "all" ? reviews : reviews.filter((r) => r.rating === parseInt(selectedRatingFilter));

  useEffect(() => {
    if (product?.product_id) {
      fetchReviews();
      checkUserCanReview();
    }
  }, [product?.product_id]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      if (mediaCardRef.current && detailsCardRef.current) {
        gsap.fromTo(
          mediaCardRef.current,
          { opacity: 0, y: 18, scale: 0.99 },
          { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "power2.out" }
        );

        gsap.fromTo(
          detailsCardRef.current,
          { opacity: 0, y: 18, scale: 0.99 },
          { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "power2.out", delay: 0.05 }
        );
      }

      if (randomSectionRef.current) {
        gsap.fromTo(
          randomSectionRef.current,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.55, ease: "power2.out", delay: 0.3 }
        );
      }
    }, pageRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (!rowsRef.current?.length) return;
    gsap.killTweensOf(rowsRef.current);

    gsap.fromTo(
      rowsRef.current.filter(Boolean),
      { opacity: 0, y: 10 },
      { opacity: 1, y: 0, duration: 0.35, ease: "power2.out", stagger: 0.04 }
    );
  }, [sizes]);

  const normalizeImageUrl = (p) => {
    if (p == null) return "";
    let s = String(p).trim();
    if (!s) return "";

    s = s.replace(/^"+|"+$/g, "").replace(/^'+|'+$/g, "");
    if (s.startsWith("http://") || s.startsWith("https://")) return s;

    s = s.replace(/^\.\//, "").replace(/^\/+/, "");

    const base = String(imgLocation || "").replace(/\/+$/, "");
    const baseEndsWithImages = /\/images$/i.test(base);
    if (baseEndsWithImages && /^images\//i.test(s)) s = s.replace(/^images\//i, "");

    return `${base}/${s}`.replace(/([^:]\/)\/+/g, "$1");
  };

  const images = useMemo(() => {
    const raw = product?.img_path ?? "";
    if (!raw) return [];

    if (Array.isArray(raw)) return raw.map(normalizeImageUrl).filter(Boolean);

    const str = String(raw).trim();
    if (!str) return [];

    try {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed)) return parsed.map(normalizeImageUrl).filter(Boolean);
    } catch (e) {}

    return str
      .split(",")
      .map((x) => normalizeImageUrl(x))
      .filter(Boolean);
  }, [product?.img_path]);

  useEffect(() => {
    thumbElsRef.current = [];
    if (!images.length) {
      setActiveIndex(0);
      return;
    }
    if (activeIndex >= images.length) setActiveIndex(0);
  }, [images.length, activeIndex]);

  const onCardEnter = (el) => el && gsap.to(el, { y: -8, duration: 0.22, ease: "power2.out" });
  const onCardLeave = (el) => el && gsap.to(el, { y: 0, duration: 0.22, ease: "power2.out" });

  const changeGrid = (clickedIndex) => {
    if (clickedIndex === activeIndex) return;
    if (images.length <= 1) {
      setActiveIndex(clickedIndex);
      return;
    }

    const els = [mainImageRef.current, ...thumbElsRef.current.filter(Boolean)].filter(Boolean);
    if (!els.length) {
      setActiveIndex(clickedIndex);
      return;
    }

    const state = Flip.getState(els);
    setActiveIndex(clickedIndex);

    Flip.from(state, { duration: 0.3, absolute: true, ease: "power1.inOut" });
  };

  const handleSizeChange = (e) => {
    const { value, checked } = e.target;
    if (product?.uses_purity_pricing) {
      setSelectedSizes(checked ? [value] : []);
      return;
    }

    setSelectedSizes((prev) => (checked ? [...prev, value] : prev.filter((s) => s !== value)));
  };

  const handleQuantityChange = (index, value) => {
    if (value === "") {
      const updated = [...quantities];
      updated[index] = "";
      setQuantities(updated);
      return;
    }

    const parsedValue = parseInt(value, 10);
    if (isNaN(parsedValue) || parsedValue <= 0 || parsedValue > 1000000) {
      alert("Please enter a valid quantity (1 to 1,000,000)");
      return;
    }

    const updated = [...quantities];
    updated[index] = parsedValue;
    setQuantities(updated);
  };

  const showMessage = (msg, type = "info") => {
  setCartMessage(msg);
  // You can also show a toast or popup here
  setTimeout(() => {
    setCartMessage("");
  }, 3000);
};

  const handleAddToCart = async () => {
  try {
    if (!selectedSizes || selectedSizes.length === 0) {
      setCartMessage(product?.uses_purity_pricing ? "Please select a purity." : "Please select at least one size.");
      return;
    }

    const quantitiesToSend = selectedSizes.map((size) => {
      const sizeIndex = sizes.indexOf(size);
      return quantities[sizeIndex] || 1;
    });

    const total_price = selectedSizes.reduce((acc, size, index) => {
      const sizeIndex = sizes.indexOf(size);
      const quantity = quantitiesToSend[index];
      const price = getEffectivePrice(sizeIndex, quantity);
      return acc + price * quantity;
    }, 0);

    console.log("Sending to cart:", {
      product_id: product.product_id,
      cart_id: cartId,
      sizes: JSON.stringify(selectedSizes),
      quantities: JSON.stringify(quantitiesToSend),
      total_price,
    });

    const response = await fetch(`${ApiURl}/addToCart.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        product_id: product.product_id,
        cart_id: cartId,
        sizes: JSON.stringify(selectedSizes),
        quantities: JSON.stringify(quantitiesToSend),
        total_price,
      }),
    });

    const data = await response.json();
    console.log("Add to cart response:", data);

    if (data.status === "success") {
      setCartMessage("Product added to cart successfully!");
      
      // Update localStorage with new cart data
      if (data.cart_items) {
        localStorage.setItem("cart", JSON.stringify(data.cart_items));
        localStorage.setItem("totalAmount", data.cart_total.toString());
        
        // Dispatch storage event for other tabs
        window.dispatchEvent(new Event("storage"));
      }
      
      // Show success message with discount info
      let discountMessage = "";
      const hasBulkDiscount = selectedSizes.some((size, index) => {
        const sizeIndex = sizes.indexOf(size);
        const qty = quantitiesToSend[index];
        return qty >= 5; // Check if any item qualifies for bulk discount
      });
      
      if (hasBulkDiscount) {
        discountMessage = " Bulk discount applied!";
      }
      
      showMessage(`Added to cart successfully!${discountMessage}`, "success");
      
      // Navigate to cart after short delay
      setTimeout(() => {
        navigate("/cart");
      }, 1000);
    } else {
      setCartMessage(data.message || "Failed to add product to cart.");
      if (data.stock_errors) {
        showMessage(data.stock_errors.join(", "), "error");
      }
    }
  } catch (error) {
    console.error("Error adding to cart:", error);
    setCartMessage("Error adding product to cart.");
    showMessage("Error adding to cart", "error");
  }
};

  const handleWishlistToggle = async () => {
    const userId = localStorage.getItem("user_id");
    if (!userId) {
      alert("Please log in to manage your wishlist.");
      return;
    }

    const productId = product?.product_id != null ? String(product.product_id) : "";
    if (!productId) return;

    if (wishlistBusy) return;
    setWishlistBusy(true);

    try {
      if (wishlistAdded) {
        await postPhp(`${ApiURl}/removeWishlistItem.php`, {
          user_id: userId,
          product_id: productId,
        });

        setWishlistAdded(false);
        window.dispatchEvent(new CustomEvent("wishlist:changed", { detail: { productId, inWishlist: false } }));
        setPopupMessage("Item Removed from Wishlist");
        setShowPopup(true);
      } else {
        await postPhp(`${ApiURl}/addWishlistItem.php`, {
          user_id: userId,
          product_id: productId,
        });

        setWishlistAdded(true);
        window.dispatchEvent(new CustomEvent("wishlist:changed", { detail: { productId, inWishlist: true } }));
        setPopupMessage("Item Added to Wishlist");
        setShowPopup(true);
      }
    } catch (error) {
      console.error("Error updating wishlist:", error);
      alert("Wishlist update failed. Please try again.");
    } finally {
      setWishlistBusy(false);
    }
  };

  const handleViewProduct = (productId) => {
    if (!productId) return;
    navigate(`/ProductOverview/${productId}`);
  };

  const thumbCols = useMemo(() => {
    const count = images.length;
    if (count <= 1) return 1;
    if (count === 2) return 2;
    if (count === 3) return 3;
    if (count === 4) return 4;
    return 5;
  }, [images.length]);

  const thumbColsClass =
    thumbCols === 1
      ? "grid-cols-1"
      : thumbCols === 2
      ? "grid-cols-2"
      : thumbCols === 3
      ? "grid-cols-3"
      : thumbCols === 4
      ? "grid-cols-4"
      : "grid-cols-5";

  const cardBase = "relative bg-white shadow-[0_15px_20px_rgba(0,0,0,0.057)] transition will-change-transform";
  const btnBase =
    "inline-flex items-center justify-center rounded-sm border border-gray-200 bg-white px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-800 transition hover:bg-gray-50";
  const btnPrimary = "border-blue-600 bg-blue-600 text-white hover:border-blue-700 hover:bg-blue-700";
  const pill = "rounded-sm bg-gray-200 px-2 py-1 text-[11px] uppercase tracking-wider text-gray-800";

  const addToCartFallback = {
    backgroundColor: "var(--user-gold, #b29063)",
    color: "var(--user-cream, #fff)",
    borderColor: "var(--user-gold, #b29063)",
  };

  const selectedIndex = selectedSizes.length
    ? Math.max(0, sizes.indexOf(selectedSizes[0]))
    : Math.max(0, availability.findIndex((item) => item !== 0));

  const selectedQty = quantities[selectedIndex] || 1;
  const selectedPrice = getEffectivePrice(selectedIndex, selectedQty);
  const originalSelectedPrice = parseFloat(prices[selectedIndex] || 0);

  const shortDescription = String(product.description || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const ratingValue = Number(reviewStats.average_rating || 0);
  const ratingRounded = Math.round(ratingValue);

  const benefitItems = [
    { icon: "truck", title: "Free Shipping", text: "Above ₹20,000" },
    { icon: "return", title: "Easy Returns", text: "7 Days Return" },
    { icon: "shield", title: "Certified Jewellery", text: "100% Hallmarked" },
    { icon: "lock", title: "Secure Payments", text: "100% Safe & Secure" },
  ];

  const renderIcon = (name, className = "") => {
    const common = {
      className,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.6",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": true,
    };

    if (name === "truck") {
      return (
        <svg {...common}>
          <path d="M3 6h11v11H3z" />
          <path d="M14 10h4l3 3v4h-7z" />
          <circle cx="7" cy="18" r="2" />
          <circle cx="18" cy="18" r="2" />
        </svg>
      );
    }

    if (name === "return") {
      return (
        <svg {...common}>
          <path d="M4 10a8 8 0 1 1 2 8" />
          <path d="M4 4v6h6" />
        </svg>
      );
    }

    if (name === "shield") {
      return (
        <svg {...common}>
          <path d="M12 3 5 6v5c0 5 3.2 8 7 10 3.8-2 7-5 7-10V6z" />
          <path d="m9 12 2 2 4-5" />
        </svg>
      );
    }

    if (name === "lock") {
      return (
        <svg {...common}>
          <rect x="5" y="10" width="14" height="10" rx="2" />
          <path d="M8 10V7a4 4 0 0 1 8 0v3" />
        </svg>
      );
    }

    return null;
  };

  useEffect(() => {
    if (!pageRef.current) return;

    const ctx = gsap.context(() => {
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduced) return;

      gsap.fromTo(
        ".po-breadcrumb",
        { opacity: 0, y: -18 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power3.out" }
      );

      gsap.fromTo(
        ".po-gallery",
        { opacity: 0, x: -42, y: 18 },
        { opacity: 1, x: 0, y: 0, duration: 0.9, ease: "power3.out" }
      );

      gsap.fromTo(
        ".po-details",
        { opacity: 0, x: 42, y: 18 },
        { opacity: 1, x: 0, y: 0, duration: 0.9, ease: "power3.out", delay: 0.08 }
      );

      gsap.utils.toArray(".po-scroll-reveal").forEach((element) => {
        gsap.fromTo(
          element,
          { opacity: 0, y: 38 },
          {
            opacity: 1,
            y: 0,
            duration: 0.85,
            ease: "power3.out",
            scrollTrigger: {
              trigger: element,
              start: "top 88%",
              toggleActions: "play none none reverse",
            },
          }
        );
      });

      gsap.utils.toArray(".po-related-card").forEach((card, index) => {
        gsap.fromTo(
          card,
          { opacity: 0, y: 35, scale: 0.97 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.72,
            delay: (index % 4) * 0.07,
            ease: "power3.out",
            scrollTrigger: {
              trigger: card,
              start: "top 91%",
              toggleActions: "play none none reverse",
            },
          }
        );
      });
    }, pageRef);

    const timer = window.setTimeout(() => ScrollTrigger.refresh(), 350);
    return () => {
      window.clearTimeout(timer);
      ctx.revert();
    };
  }, [product?.product_id, images.length, randomProducts.length]);

  return (
    <div ref={pageRef} className="po-page" style={{ backgroundColor: "var(--user-bg, #E8D9D5)" }}>
      <style>{`
        .po-page,
        .po-page * { box-sizing: border-box; }
        .po-page {
          --po-maroon:var(--user-maroon, #2B0000);
          --po-maroon-dark:var(--user-maroon-deep, #170000);
          --po-gold:var(--user-gold, #C89432);
          --po-gold-soft:var(--user-gold-soft, #E5B957);
          --po-cream:var(--user-cream, #FFF9F2);
          --po-paper:var(--user-card, #FBF4EC);
          --po-line:var(--user-border, #E6D2BD);
          --po-text:var(--user-text, #2A1515);
          --po-muted:var(--user-text-soft, #6F5B54);
          min-height:100vh;
          width:100%;
          overflow-x:hidden;
          overflow-y:visible;
          color:var(--po-text);
          font-family:Arial, Helvetica, sans-serif;
          padding-bottom:46px;
        }
        .po-serif { font-family:Georgia, "Times New Roman", serif; }
        .po-shell { width:min(1480px, calc(100% - 34px)); margin:0 auto; }
        .po-breadcrumb {
          min-height:72px;
          display:flex;
          align-items:center;
          padding:0 28px;
          color:#fff7eb;
          background:
            linear-gradient(90deg, rgba(28,0,0,.98), rgba(45,0,0,.8)),
            radial-gradient(circle at 84% 50%, rgba(141,19,25,.42), transparent 38%);
          border-bottom:1px solid rgba(201,148,50,.5);
          box-shadow:0 14px 35px rgba(43,0,0,.16);
        }
        .po-breadcrumb-list { display:flex; flex-wrap:wrap; align-items:center; gap:10px; font-size:13px; }
        .po-breadcrumb-list button { color:inherit; background:transparent; border:0; cursor:pointer; padding:0; }
        .po-breadcrumb-list button:hover { color:var(--po-gold-soft); }
        .po-breadcrumb-sep { color:var(--po-gold-soft); }
        .po-main {
          display:grid;
          grid-template-columns:minmax(0, .82fr) minmax(430px, 1.18fr);
          gap:44px;
          align-items:start;
          padding:42px 0 30px;
        }
        .po-gallery { min-width:0; }
        .po-main-image {
          position:relative;
          aspect-ratio:1.18/1;
          max-height:min(560px, calc(100vh - 190px));
          overflow:hidden;
          border:1px solid var(--po-line);
          border-radius:13px;
          background:#f6ecdf;
          box-shadow:0 18px 48px rgba(43,0,0,.1);
        }
        .po-main-image img { width:100%; height:100%; object-fit:contain; display:block; }
        .po-main-badge {
          position:absolute; left:16px; top:16px; z-index:3;
          padding:7px 12px; border-radius:5px;
          color:#3b1600; background:#f2c976;
          font-size:12px; font-weight:700; letter-spacing:.05em;
        }
        .po-wishlist-float {
          position:absolute; right:16px; top:16px; z-index:3;
          width:40px; height:40px; display:grid; place-items:center;
          border:1px solid rgba(43,0,0,.08); border-radius:50%;
          color:var(--po-maroon); background:rgba(255,255,255,.94);
          cursor:pointer; font-size:21px; transition:.25s ease;
        }
        .po-wishlist-float:hover { transform:translateY(-2px); box-shadow:0 10px 20px rgba(43,0,0,.15); }
        .po-arrow {
          position:absolute; top:50%; z-index:3; transform:translateY(-50%);
          width:42px; height:42px; border:1px solid var(--po-line); border-radius:50%;
          color:var(--po-maroon); background:rgba(255,255,255,.94); cursor:pointer;
          display:grid; place-items:center; font-size:24px; transition:.25s ease;
        }
        .po-arrow:hover { background:var(--po-maroon); color:white; }
        .po-arrow-left { left:15px; }
        .po-arrow-right { right:15px; }
        .po-thumbs { display:grid; grid-template-columns:repeat(6, minmax(0,1fr)); gap:10px; margin-top:12px; }
        .po-thumb {
          aspect-ratio:1.1/1; overflow:hidden; border:1px solid var(--po-line); border-radius:7px;
          background:#f7eee4; cursor:pointer; transition:.25s ease;
        }
        .po-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
        .po-thumb-active { border-color:var(--po-maroon); box-shadow:0 0 0 2px rgba(43,0,0,.12); }
        .po-details { min-width:0; padding:7px 4px 0; }
        .po-product-title { margin:0; color:var(--po-maroon); font-size:clamp(34px,3vw,52px); font-weight:400; line-height:1.06; }
        .po-rating { display:flex; align-items:center; gap:10px; margin-top:14px; color:var(--po-muted); font-size:14px; }
        .po-stars { color:#E5A82F; letter-spacing:2px; font-size:18px; }
        .po-price { margin-top:19px; color:var(--po-maroon); font-size:clamp(28px,2.25vw,39px); font-family:Georgia,serif; }
        .po-original-price { margin-left:10px; color:#9A8980; font-size:17px; text-decoration:line-through; }
        .po-tax { margin-top:6px; color:#876756; font-size:13px; }
        .po-description { margin-top:25px; color:#493633; font-size:15px; line-height:1.72; max-width:690px; }
        .po-rule { height:1px; margin:27px 0 21px; background:linear-gradient(90deg,var(--po-gold),var(--po-line),transparent); position:relative; }
        .po-rule:before { content:""; position:absolute; left:0; top:50%; width:7px; height:7px; border:1px solid var(--po-gold); transform:translate(-1px,-50%) rotate(45deg); background:var(--po-paper); }
        .po-option-label { display:block; margin-bottom:10px; color:#331c1a; font-size:13px; font-weight:600; }
        .po-size-grid { display:flex; flex-wrap:wrap; gap:9px; }
        .po-size-chip {
          position:relative; display:inline-flex; align-items:center; justify-content:center; min-width:74px;
          min-height:40px; padding:8px 14px; border:1px solid var(--po-line); border-radius:6px;
          color:#3d2925; background:#fffaf5; cursor:pointer; font-size:13px; transition:.2s ease;
        }
        .po-size-chip input { position:absolute; opacity:0; pointer-events:none; }
        .po-size-chip-selected { border-color:var(--po-maroon); box-shadow:0 0 0 1px rgba(43,0,0,.12); }
        .po-size-chip-disabled { opacity:.42; cursor:not-allowed; }
        .po-purchase-row { display:grid; grid-template-columns:146px 1fr; gap:14px; margin-top:23px; }
        .po-qty { display:grid; grid-template-columns:42px minmax(54px,1fr) 42px; border:1px solid var(--po-line); border-radius:6px; overflow:hidden; background:white; }
        .po-qty button { border:0; background:#fffaf5; color:var(--po-maroon); cursor:pointer; font-size:19px; }
        .po-qty input { width:100%; min-width:0; border:0; border-left:1px solid var(--po-line); border-right:1px solid var(--po-line); text-align:center; outline:none; background:white; color:var(--po-maroon); font-size:16px; font-weight:700; appearance:textfield; -moz-appearance:textfield; }
        .po-qty input::-webkit-outer-spin-button,
        .po-qty input::-webkit-inner-spin-button { margin:0; -webkit-appearance:none; }
        .po-primary-btn {
          min-height:52px; border:1px solid var(--po-maroon); border-radius:6px;
          background:linear-gradient(90deg,var(--po-maroon),var(--po-maroon-dark),var(--po-maroon)); color:var(--po-cream);
          cursor:pointer; font-size:14px; font-weight:600; letter-spacing:.03em; transition:.25s ease;
        }
        .po-primary-btn:hover { transform:translateY(-2px); box-shadow:0 14px 28px rgba(43,0,0,.2); }
        .po-buy-btn { width:100%; min-height:49px; margin-top:13px; border:1px solid var(--po-gold); border-radius:6px; color:#8A4D00; background:transparent; cursor:pointer; font-weight:600; }
        .po-wishlist-row { display:flex; align-items:center; justify-content:center; gap:8px; margin-top:20px; border:0; background:transparent; color:#884B2A; cursor:pointer; }
        .po-benefits {
          display:grid; grid-template-columns:repeat(4, minmax(0,1fr)); margin-top:42px;
          border:1px solid var(--po-line); border-radius:12px; background:rgba(255,250,245,.68);
          box-shadow:0 12px 32px rgba(43,0,0,.05); overflow:hidden;
        }
        .po-benefit { display:flex; align-items:center; gap:13px; min-height:90px; padding:18px 24px; }
        .po-benefit:not(:last-child) { border-right:1px solid var(--po-line); }
        .po-benefit svg { width:31px; height:31px; color:#8E4B2A; flex:none; }
        .po-benefit h4 { margin:0 0 5px; font-size:13px; }
        .po-benefit p { margin:0; color:var(--po-muted); font-size:12px; }
        .po-info-card {
          margin-top:34px; padding:0 28px 27px; border:1px solid var(--po-line); border-radius:12px;
          background:rgba(255,250,245,.72); box-shadow:0 12px 32px rgba(43,0,0,.05);
        }
        .po-tabs { display:grid; grid-template-columns:repeat(4,1fr); border-bottom:1px solid var(--po-line); }
        .po-tab { min-height:72px; border:0; background:transparent; color:#352321; font-size:13px; font-weight:600; letter-spacing:.02em; }
        .po-tab:first-child { color:var(--po-maroon); position:relative; }
        .po-tab:first-child:after { content:""; position:absolute; left:0; right:0; bottom:-1px; height:2px; background:var(--po-gold); }
        .po-info-content { display:grid; grid-template-columns:1.08fr .92fr; gap:45px; align-items:center; padding:30px 14px 0; }
        .po-info-copy { color:#4B3732; font-size:14px; line-height:1.7; }
        .po-info-copy ul { margin:14px 0 0; padding:0; list-style:none; }
        .po-info-copy li { position:relative; padding-left:17px; margin:7px 0; }
        .po-info-copy li:before { content:""; position:absolute; left:0; top:.63em; width:5px; height:5px; border-radius:50%; background:var(--po-gold); }
        .po-info-image { aspect-ratio:1.75/1; overflow:hidden; border-radius:11px; background:#f5eadf; }
        .po-info-image img { width:100%; height:100%; object-fit:cover; }
        .po-section-heading { display:flex; align-items:center; justify-content:center; gap:17px; margin:43px 0 25px; }
        .po-section-heading span { width:90px; height:1px; background:linear-gradient(90deg,transparent,var(--po-gold)); }
        .po-section-heading span:last-child { background:linear-gradient(90deg,var(--po-gold),transparent); }
        .po-section-heading h2 { margin:0; color:var(--po-maroon); font-size:29px; font-weight:400; }
        .po-related-grid { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:20px; }
        .po-related-card {
          overflow:hidden; border:1px solid var(--po-line); border-radius:11px; background:rgba(255,250,245,.82);
          box-shadow:0 10px 24px rgba(43,0,0,.06); cursor:pointer; transition:.25s ease;
        }
        .po-related-card:hover { transform:translateY(-6px); box-shadow:0 18px 35px rgba(43,0,0,.12); }
        .po-related-image { position:relative; aspect-ratio:1.1/1; overflow:hidden; background:#f4e8db; }
        .po-related-image img { width:100%; height:100%; object-fit:cover; transition:transform .6s ease; }
        .po-related-card:hover img { transform:scale(1.06); }
        .po-related-heart { position:absolute; top:10px; right:10px; width:32px; height:32px; display:grid; place-items:center; border-radius:50%; background:white; color:var(--po-maroon); }
        .po-related-body { padding:14px 15px 16px; }
        .po-related-name { margin:0 0 8px; color:#301919; font-family:Georgia,serif; font-size:17px; line-height:1.25; }
        .po-related-price { color:#301919; font-size:14px; font-weight:700; }
        .po-related-rating { margin-top:9px; color:#E0A42D; font-size:13px; }
        .po-reviews { margin-top:38px; padding:26px; border:1px solid var(--po-line); border-radius:12px; background:rgba(255,250,245,.7); }
        .po-review-header { display:flex; align-items:center; justify-content:space-between; gap:18px; }
        .po-review-header h2 { margin:0; color:var(--po-maroon); font-family:Georgia,serif; font-size:28px; font-weight:400; }
        .po-review-btn { border:1px solid var(--po-maroon); border-radius:6px; padding:11px 18px; background:var(--po-maroon); color:white; cursor:pointer; }
        .po-review-list { margin-top:22px; display:grid; gap:14px; }
        .po-message { margin:18px 0 0; padding:11px 14px; border:1px solid var(--po-line); border-radius:6px; background:#fff9f1; color:#6b3f26; font-size:13px; }
        .po-mobile-only { display:none; }

        @media (max-width:1024px) {
          .po-main { grid-template-columns:1fr 1fr; gap:26px; }
          .po-main-image { max-height:480px; }
          .po-shell { width:min(100% - 24px, 1180px); }
          .po-benefit { padding:16px 13px; }
          .po-related-grid { grid-template-columns:repeat(3,minmax(0,1fr)); }
        }

        @media (max-width:767px) {
          .po-page { padding-bottom:24px; }
          .po-shell { width:100%; }
          .po-breadcrumb { min-height:64px; padding:13px 14px; align-items:flex-end; }
          .po-breadcrumb-list { font-size:11px; gap:7px; line-height:1.55; }
          .po-main { display:block; padding:10px 8px 0; }
          .po-gallery { margin:0; }
          .po-main-image { aspect-ratio:.98/1; max-height:none; border-radius:8px; }
          .po-main-image img { object-fit:cover; }
          .po-main-badge { left:10px; top:10px; padding:6px 9px; font-size:10px; }
          .po-wishlist-float { right:10px; top:10px; width:34px; height:34px; }
          .po-arrow { width:34px; height:34px; font-size:19px; }
          .po-arrow-left { left:8px; } .po-arrow-right { right:8px; }
          .po-thumbs { grid-template-columns:repeat(5,minmax(0,1fr)); gap:6px; margin-top:7px; overflow:hidden; }
          .po-thumb { border-radius:5px; }
          .po-details { padding:18px 6px 0; }
          .po-product-title { font-size:27px; }
          .po-rating { margin-top:8px; font-size:12px; }
          .po-stars { font-size:15px; }
          .po-price { font-size:25px; margin-top:14px; }
          .po-original-price { font-size:14px; }
          .po-description { margin-top:18px; font-size:13px; line-height:1.65; }
          .po-rule { margin:20px 0 16px; }
          .po-size-chip { min-width:64px; min-height:35px; padding:6px 10px; font-size:11px; }
          .po-purchase-row { grid-template-columns:120px 1fr; gap:9px; margin-top:18px; }
          .po-primary-btn { min-height:45px; font-size:12px; }
          .po-buy-btn { min-height:43px; font-size:12px; }
          .po-wishlist-row { font-size:12px; margin-top:16px; }
          .po-benefits { grid-template-columns:repeat(2,1fr); margin:25px 8px 0; border-radius:9px; }
          .po-benefit { min-height:94px; padding:15px 12px; display:block; text-align:center; }
          .po-benefit:nth-child(odd) { border-right:1px solid var(--po-line); }
          .po-benefit:nth-child(-n+2) { border-bottom:1px solid var(--po-line); }
          .po-benefit:not(:last-child) { border-right:0; }
          .po-benefit svg { width:27px; height:27px; margin:0 auto 8px; }
          .po-benefit h4 { font-size:11px; }
          .po-benefit p { font-size:10px; }
          .po-info-card { margin:24px 8px 0; padding:0; overflow:hidden; border-radius:9px; }
          .po-tabs { display:block; border:0; }
          .po-tab { width:100%; min-height:51px; padding:0 14px; text-align:left; border-bottom:1px solid var(--po-line); font-size:12px; display:flex; justify-content:space-between; align-items:center; }
          .po-tab:after { content:"⌄"; color:var(--po-maroon); }
          .po-tab:first-child:after { position:static; width:auto; height:auto; background:none; }
          .po-info-content { display:none; }
          .po-section-heading { margin:31px 0 17px; gap:9px; }
          .po-section-heading span { width:35px; }
          .po-section-heading h2 { font-size:21px; }
          .po-related-grid { grid-template-columns:repeat(3,minmax(125px,1fr)); gap:9px; overflow-x:auto; padding:0 8px 7px; scroll-snap-type:x mandatory; }
          .po-related-card { scroll-snap-align:start; border-radius:8px; }
          .po-related-body { padding:9px 9px 11px; }
          .po-related-name { font-size:13px; }
          .po-related-price { font-size:11px; }
          .po-related-rating { font-size:10px; }
          .po-reviews { margin:26px 8px 0; padding:17px 13px; }
          .po-review-header h2 { font-size:22px; }
          .po-review-btn { padding:9px 12px; font-size:11px; }
          .po-desktop-only { display:none !important; }
          .po-mobile-only { display:block; }
        }
      `}</style>

      <div className="po-breadcrumb po-breadcrumb">
        <div className="po-shell">
          <div className="po-breadcrumb-list">
            <button type="button" onClick={() => navigate("/")}>Home</button>
            <span className="po-breadcrumb-sep">›</span>
            <button type="button" onClick={() => navigate("/all-products")}>Shop</button>
            <span className="po-breadcrumb-sep">›</span>
            <span>{product.category_name || "Collection"}</span>
            <span className="po-breadcrumb-sep">›</span>
            <span>{product.name || "Product"}</span>
          </div>
        </div>
      </div>

      <div className="po-shell">
        <section className="po-main">
          <div ref={mediaCardRef} className="po-gallery">
            <div ref={mainImageRef} className="po-main-image">
              <span className="po-main-badge">NEW</span>
              <button
                type="button"
                className="po-wishlist-float"
                onClick={handleWishlistToggle}
                disabled={wishlistBusy}
                aria-label={wishlistAdded ? "Remove from wishlist" : "Add to wishlist"}
              >
                {wishlistAdded ? "♥" : "♡"}
              </button>

              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    className="po-arrow po-arrow-left"
                    onClick={() => changeGrid((activeIndex - 1 + images.length) % images.length)}
                    aria-label="Previous image"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="po-arrow po-arrow-right"
                    onClick={() => changeGrid((activeIndex + 1) % images.length)}
                    aria-label="Next image"
                  >
                    ›
                  </button>
                </>
              )}

              <img
                src={images[activeIndex] || FALLBACK_IMG}
                alt={product.name || "Product"}
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = FALLBACK_IMG;
                }}
              />
            </div>

            {images.length > 1 && (
              <div className="po-thumbs">
                {images.slice(0, 6).map((src, index) => (
                  <button
                    type="button"
                    key={`${src}-${index}`}
                    ref={(element) => (thumbElsRef.current[index] = element)}
                    className={`po-thumb ${index === activeIndex ? "po-thumb-active" : ""}`}
                    onClick={() => changeGrid(index)}
                    aria-label={`Show image ${index + 1}`}
                  >
                    <img src={src || FALLBACK_IMG} alt={`Thumbnail ${index + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div ref={detailsCardRef} className="po-details">
            <h1 className="po-product-title po-serif">{product.name || "Product"}</h1>

            <div className="po-rating">
              <span className="po-stars">
                {[1, 2, 3, 4, 5].map((star) => (star <= ratingRounded ? "★" : "☆")).join("")}
              </span>
              <span>({reviewStats.total_reviews || 0} Reviews)</span>
            </div>

            <div className="po-price">
              ₹{safeFormatPrice(selectedPrice || firstAvailablePrice)}
              {originalSelectedPrice > selectedPrice && (
                <span className="po-original-price">₹{safeFormatPrice(originalSelectedPrice)}</span>
              )}
            </div>
            <div className="po-tax">Inclusive of all taxes</div>

            <p className="po-description">
              {shortDescription || "Discover refined craftsmanship, timeless detailing and premium quality in this exclusive S. L. GOLD creation."}
            </p>

            <div className="po-rule" />

            <div>
              <span className="po-option-label">
                {product?.uses_purity_pricing ? "Select Purity" : "Select Size"}
              </span>
              <div className="po-size-grid">
                {sizes.map((size, index) => {
                  const checked = selectedSizes.includes(size);
                  const unavailable = availability[index] === 0;
                  return (
                    <label
                      key={`${size}-${index}`}
                      className={`po-size-chip ${checked ? "po-size-chip-selected" : ""} ${unavailable ? "po-size-chip-disabled" : ""}`}
                    >
                      <input
                        type={product?.uses_purity_pricing ? "radio" : "checkbox"}
                        name={product?.uses_purity_pricing ? "product-purity" : undefined}
                        value={size}
                        checked={checked}
                        disabled={unavailable}
                        onChange={handleSizeChange}
                      />
                      {size}
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="po-purchase-row">
              <div className="po-qty">
                <button
                  type="button"
                  onClick={() => handleQuantityChange(selectedIndex, Math.max(1, selectedQty - 1))}
                >
                  −
                </button>
                <input
                  type="number"
                  value={selectedQty}
                  min="1"
                  onChange={(event) => handleQuantityChange(selectedIndex, event.target.value)}
                />
                <button
                  type="button"
                  onClick={() => handleQuantityChange(selectedIndex, selectedQty + 1)}
                >
                  +
                </button>
              </div>

              <button type="button" className="po-primary-btn" onClick={handleAddToCart}>
                Add to Cart
              </button>
            </div>

            <button type="button" className="po-buy-btn" onClick={handleAddToCart}>
              Buy Now
            </button>

            <button
              type="button"
              className="po-wishlist-row"
              onClick={handleWishlistToggle}
              disabled={wishlistBusy}
            >
              <span>{wishlistAdded ? "♥" : "♡"}</span>
              <span>{wishlistAdded ? "Remove from Wishlist" : "Add to Wishlist"}</span>
            </button>

            {cartMessage && <div className="po-message">{cartMessage}</div>}
          </div>
        </section>

        <section className="po-benefits po-scroll-reveal">
          {benefitItems.map((item) => (
            <article className="po-benefit" key={item.title}>
              {renderIcon(item.icon)}
              <div>
                <h4>{item.title}</h4>
                <p>{item.text}</p>
              </div>
            </article>
          ))}
        </section>

        <section className="po-info-card po-scroll-reveal">
          <div className="po-tabs">
            <button className="po-tab" type="button">Description</button>
            <button className="po-tab" type="button">Specifications</button>
            <button className="po-tab" type="button">Shipping &amp; Returns</button>
            <button className="po-tab" type="button">Care Instructions</button>
          </div>

          <div className="po-info-content">
            <div className="po-info-copy">
              <p>
                {shortDescription || "A premium jewellery creation designed with refined detailing and timeless elegance."}
              </p>
              <ul>
                <li>Premium craftsmanship with intricate detailing</li>
                <li>Designed for timeless elegance and daily comfort</li>
                <li>Suitable for weddings, festivals and special occasions</li>
                <li>Authenticity-focused finish and premium presentation</li>
              </ul>
            </div>

            <div className="po-info-image">
              <img src={images[1] || images[0] || FALLBACK_IMG} alt={`${product.name || "Product"} detail`} />
            </div>
          </div>
        </section>

        <section ref={randomSectionRef} className="po-scroll-reveal">
          <div className="po-section-heading">
            <span />
            <h2 className="po-serif">You May Also Like</h2>
            <span />
          </div>

          {loadingRandom ? (
            <div className="po-related-grid">
              {[1, 2, 3, 4].map((item) => (
                <div key={item} className="po-related-card" style={{ minHeight: 300, opacity: 0.55 }} />
              ))}
            </div>
          ) : randomProducts.length > 0 ? (
            <div className="po-related-grid">
              {randomProducts.slice(0, 4).map((item, index) => {
                const image = (() => {
                  const raw = item?.img_path || item?.image || item?.image_path;
                  if (!raw) return FALLBACK_IMG;
                  if (Array.isArray(raw)) return normalizeImageUrl(raw[0]) || FALLBACK_IMG;
                  if (typeof raw === "string") {
                    if (/^https?:\/\//i.test(raw)) return raw;
                    try {
                      const parsed = JSON.parse(raw);
                      if (Array.isArray(parsed)) return normalizeImageUrl(parsed[0]) || FALLBACK_IMG;
                    } catch (error) {}
                    return normalizeImageUrl(raw.split(",")[0]) || FALLBACK_IMG;
                  }
                  return FALLBACK_IMG;
                })();

                const itemTitle = item.name || item.product_name || "Jewellery";
                const itemId = item.product_id || item.id;
                const itemPrice = parseFloat(item.min_price || item.price || item.product_price || 0);

                return (
                  <article
                    key={`${itemId}-${index}`}
                    className="po-related-card"
                    onClick={() => handleViewProduct(itemId)}
                  >
                    <div className="po-related-image">
                      <img src={image} alt={itemTitle} />
                      <span className="po-related-heart">♡</span>
                    </div>
                    <div className="po-related-body">
                      <h3 className="po-related-name">{itemTitle}</h3>
                      <div className="po-related-price">
                        {itemPrice > 0 ? `₹${Math.round(itemPrice).toLocaleString("en-IN")}` : "Price on request"}
                      </div>
                      <div className="po-related-rating">★★★★★</div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="po-message" style={{ textAlign: "center" }}>
              No additional products found at the moment.
            </div>
          )}
        </section>

        <section className="po-reviews po-scroll-reveal">
          <div className="po-review-header">
            <h2>Customer Reviews</h2>
            {(userPurchaseStatus.can_review || isAdmin) && (
              <button
                type="button"
                className="po-review-btn"
                onClick={() => {
                  setEditingReview(userPurchaseStatus.existing_review || null);
                  setShowReviewModal(true);
                }}
              >
                {userPurchaseStatus.existing_review ? "Edit Review" : "Write a Review"}
              </button>
            )}
          </div>

          <div className="po-review-list">
            {loadingReviews ? (
              <div className="po-message">Loading reviews...</div>
            ) : filteredReviews.length > 0 ? (
              filteredReviews.map((review) => (
                <ReviewCard
                  key={review.id}
                  review={review}
                  currentUserId={localStorage.getItem("user_id")}
                  isAdmin={(localStorage.getItem("role") || "").toLowerCase() === "admin"}
                  onEdit={handleEditReview}
                  onDelete={handleDeleteReview}
                  onHelpful={handleHelpful}
                />
              ))
            ) : (
              <div className="po-message">No reviews yet. Be the first to review this product.</div>
            )}
          </div>
        </section>
      </div>

      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setEditingReview(null);
        }}
        productId={product?.product_id}
        existingReview={editingReview}
        onSubmitSuccess={handleReviewSuccess}
        isAdmin={(localStorage.getItem("role") || "").toLowerCase() === "admin"}
      />

      <AnimatePresence>
        {showPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/50 p-5"
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              className="w-full max-w-[390px] rounded-xl border border-[#E6D2BD] bg-[#FFF9F2] p-6 text-center shadow-2xl"
            >
              <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-[var(--user-maroon,#2B0000)] text-2xl text-[var(--user-gold-soft,#E5B957)]">
                ♥
              </div>
              <h2 className="mb-5 font-serif text-xl text-[var(--user-maroon,#2B0000)]">{popupMessage}</h2>
              <button
                type="button"
                onClick={() => setShowPopup(false)}
                className="rounded-md bg-[var(--user-maroon,#2B0000)] px-7 py-3 text-sm font-semibold text-[var(--user-cream,#fff)]"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductOverview;
